import { Box, Container, Stack, styled } from '@mui/material';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { ReactElement, useEffect, useState } from 'react';
import { useIsMobile } from '@synergy/components-next';
import { useNavigate, useParams } from 'react-router';

import { ROUTES } from '../../utils/routes';
import { WishListDropdown } from './wish-list-dropdown';
import { WishListId } from '../../models/wish-list-list-item';
import { WishListModal } from './modals/wish-list-modal';
import { fetchWishLists } from '../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { wishListDetailSelector, wishListsSelector } from '../../store/whish-list-selectors';

const StyledWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    width: '100%',
    borderTop: `1.5px solid ${theme.palette.grey[300]}`,
}));

interface WishListSelectionProps {
    onEditWishList: (id: WishListId) => void;
}

export const WishListSelection = (props: WishListSelectionProps): ReactElement => {
    const dispatch = useAppDispatch();
    const wishLists = useAppSelector(wishListsSelector);
    const updatedAt = useAppSelector(wishListDetailSelector)?.updatedAt;
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';

    const [showCreateWishListModal, setShowCreateWishListModal] = useState(false);

    useEffect(() => {
        dispatch(fetchWishLists());
    }, [dispatch]);

    useEffect(() => {
        if (wishLists.length > 0 && !wishLists.some(list => list.id === wishListId)) {
            const newId = wishLists.find(list => list.isDefault)?.id ?? wishLists[0].id;
            navigate(ROUTES.wishList.detail.replace(':id', newId.toString()), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, wishLists]);

    const handleListChange = (listId: WishListId): void => {
        navigate(ROUTES.wishList.detail.replace(':id', listId.toString()));
    };

    return (
        <StyledWrapper>
            <Container maxWidth="xl" sx={{ pt: 2, pb: 2.5 }}>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    justifyContent="space-between"
                    sx={{ width: '100%', pt: 3 }}
                    alignItems="start"
                >
                    <Box sx={{ maxWidth: 742, width: '100%' }}>
                        <WishListDropdown
                            wishLists={wishLists}
                            value={wishListId}
                            onChange={handleListChange}
                            onAddWishList={() => setShowCreateWishListModal(true)}
                            onEditWishList={props.onEditWishList}
                        />
                    </Box>
                    {updatedAt && (
                        <Stack
                            gap={isMobile ? 0.5 : 0}
                            direction={isMobile ? 'row' : 'column'}
                            textAlign="right"
                            typography="body2"
                            sx={{ py: { xs: 0, sm: 1 } }}
                        >
                            <strong>
                                <FormattedMessage id="app.wishList.changed" />
                            </strong>
                            <FormattedDate
                                value={updatedAt}
                                day="numeric"
                                month="short"
                                year="numeric"
                                hour="numeric"
                                minute="numeric"
                            />
                        </Stack>
                    )}
                </Stack>
            </Container>

            <WishListModal open={showCreateWishListModal} onClose={() => setShowCreateWishListModal(false)} />
        </StyledWrapper>
    );
};
